import React from 'react';

export interface UseIntersectionObserverArgs {
  ref: React.RefObject<HTMLElement>;
  onIntersection?: () => void;
  isObserved?: boolean;
  threshold?: number;
}
/**
 * Whether `index` is the row an infinite list should hand to
 * `useIntersectionObserver` as its "fetch the next page" trigger.
 *
 * `loadedCount` is the length of what is RENDERED — never the server's `total`.
 * While pages remain unfetched the two differ, and `total - 1` names an index
 * no rendered row holds, so the observer attaches to nothing and the list stays
 * frozen on page one.
 */
export function isLastLoadedItem(
  index: number,
  loadedCount: number,
  hasNextPage: boolean | undefined,
) {
  return Boolean(hasNextPage) && loadedCount > 0 && index === loadedCount - 1;
}

export function useIntersectionObserver({
  isObserved,
  onIntersection,
  ref,
  // Any pixel of the sentinel, not all of them. A full-visibility default
  // (1.0) is defeated by subpixel layout: a last card sitting flush with the
  // bottom of its scroll container reports 0.9997, so the callback is never
  // delivered and the list never advances past its first page. Callers that
  // want a stricter trigger pass `threshold` explicitly.
  threshold = 0,
}: UseIntersectionObserverArgs) {
  React.useEffect(() => {
    if (!isObserved || !ref.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            onIntersection?.();
          }
        });
      },
      { threshold },
    );

    observer.observe(ref.current);

    // Clean up the observer on component unmount
    return () => {
      observer.disconnect();
    };
  }, [isObserved, onIntersection, ref, threshold]);
}
